import { useRef } from 'react';
import { useI18nContext } from '@i18n/i18n-react';
import { Button } from '@components/button/Button';
import { Plus, Tick, CircledChecked, Delete, Palette } from '@components/icons/FarseerIcons';
import { ContextMenu, useContextMenuState } from '@components/context-menu/ContextMenu';
import { useColorContext } from '@providers/ColorProvider';
import { sameRepeating } from '@util/array';

import { predefinedDataPalettes } from '@base/useTheme';

import { ColorBadge } from './ColorBadge';

const PALETTE_DISPLAY_SIZE = 9;
const PALETTE_SAVE_SIZE = 30;

export type PalettePickerProps = {
    value: string[];
    disabled?: boolean;
    onChange?: (colors: string[]) => void;
};

const EmptyIcon = () => <div style={{ width: 24 }} />;

export const PalettePicker = (props: PalettePickerProps) => {
    const { disabled, value, onChange } = props;

    const { LL } = useI18nContext();
    const { customPalettes, addCustomPalette, removeCustomPalette, setDefaultPalette } = useColorContext();

    const buttonRef = useRef<HTMLButtonElement>(null);
    const { menuState, menuHandlers } = useContextMenuState({ click: 'left' });

    const swatches = predefinedDataPalettes;

    const selectedSwatchIndex = swatches.findIndex((palette) => sameRepeating(palette)(value));
    const selectedCustomIndex = customPalettes.findIndex((palette) => sameRepeating(palette)(value));

    const canSave =
        selectedSwatchIndex < 0 &&
        (selectedCustomIndex < 0 ||
            customPalettes[selectedCustomIndex].length < Math.min(value.length, PALETTE_SAVE_SIZE));

    const renderSwatches = (palette: string[]) =>
        palette.map((color, i) => <ColorBadge key={i.toString()} color={color} round disabled />);

    const renderPaletteItem = (palette: string[], i: number, selectedIndex: number, custom: boolean = false) => (
        <ContextMenu.Item
            key={i.toString()}
            item={{
                icon: selectedIndex === i ? Tick : EmptyIcon,
                isHoverable: true,
                render: () => <>{renderSwatches(palette.slice(0, PALETTE_DISPLAY_SIZE))}</>,
                action: () => onChange?.(palette.slice(0, PALETTE_SAVE_SIZE)),
            }}
        >
            <ContextMenu.Item
                item={{
                    icon: CircledChecked,
                    label: LL.SET_AS_DEFAULT_PALETTE(),
                    action: () => setDefaultPalette(palette),
                }}
            />
            {custom ? (
                <>
                    <ContextMenu.Divider />
                    <ContextMenu.Item
                        item={{
                            label: LL.DELETE_PALETTE(),
                            icon: Delete,
                            action: () => removeCustomPalette(palette),
                        }}
                    />
                </>
            ) : null}
        </ContextMenu.Item>
    );

    return (
        <>
            <Button
                {...menuHandlers}
                icon={Palette}
                ref={buttonRef}
                disabled={disabled}
                $type="quaternary"
                $size="small"
            />
            <ContextMenu {...menuState} anchorRef={buttonRef} placement="bottom-start">
                <ContextMenu.Path>{LL.STANDARD()}</ContextMenu.Path>
                {swatches.map((palette, i) => renderPaletteItem(palette, i, selectedSwatchIndex))}
                {customPalettes.length ? (
                    <>
                        <ContextMenu.Divider />
                        <ContextMenu.Path>{LL.CUSTOM()}</ContextMenu.Path>
                        {customPalettes.map((palette, i) => renderPaletteItem(palette, i, selectedCustomIndex, true))}
                    </>
                ) : null}
                <ContextMenu.Divider />
                <ContextMenu.Item
                    item={{
                        label: LL.SAVE_CURRENT_COLORS_AS_PALETTE(),
                        icon: Plus,
                        action: () => addCustomPalette(value),
                        disabled: !canSave,
                    }}
                />
            </ContextMenu>
        </>
    );
};
